export type ExplorerProgramRankingRow = {
  key: string;
  programName: string;
  sampleChannelId?: string;
  sampleChannelImageS3Key?: string;
  imageS3Key?: string;
  imageUpdatedAt?: string | Date;
};

export type PodcastProgramImageMeta = {
  imageS3Key: string;
  imageUpdatedAt?: string | Date;
};

export type LivestreamProgramRowImages = {
  /** Program cover when available (API row or podcast_programs catalog). */
  programImage?: PodcastProgramImageMeta;
  /** Channel image from sample channel (for badge overlay). */
  channelImageS3Key?: string;
};

export function buildPodcastProgramImageByName(
  programs: Array<{
    name: string;
    imageS3Key?: string | null;
    imageUpdatedAt?: string | Date | null;
  }>,
): Map<string, PodcastProgramImageMeta> {
  const map = new Map<string, PodcastProgramImageMeta>();
  for (const program of programs) {
    const key = program.name.trim().toLowerCase();
    const imageS3Key = program.imageS3Key?.trim();
    if (!key || !imageS3Key || map.has(key)) continue;
    map.set(key, {
      imageS3Key,
      ...(program.imageUpdatedAt
        ? { imageUpdatedAt: program.imageUpdatedAt }
        : {}),
    });
  }
  return map;
}

function resolveChannelImageS3Key(
  row: ExplorerProgramRankingRow,
  imageByChannelId?: Map<string, string | undefined>,
): string | undefined {
  return (
    row.sampleChannelImageS3Key?.trim() ??
    (row.sampleChannelId
      ? imageByChannelId?.get(String(row.sampleChannelId))?.trim()
      : undefined)
  );
}

/**
 * Resolve program cover and channel badge images separately.
 * Program image: row.imageS3Key or podcast_programs catalog by name.
 * Channel image: sampleChannelImageS3Key / imageByChannelId.
 */
export function resolveLivestreamProgramRowImages(
  row: ExplorerProgramRankingRow,
  imageByProgramName: Map<string, PodcastProgramImageMeta>,
  imageByChannelId?: Map<string, string | undefined>,
): LivestreamProgramRowImages {
  let programImage: PodcastProgramImageMeta | undefined;
  if (row.imageS3Key?.trim()) {
    programImage = {
      imageS3Key: row.imageS3Key.trim(),
      ...(row.imageUpdatedAt ? { imageUpdatedAt: row.imageUpdatedAt } : {}),
    };
  } else {
    programImage = imageByProgramName.get(
      row.programName.trim().toLowerCase(),
    );
  }

  const channelImageS3Key = resolveChannelImageS3Key(row, imageByChannelId);

  return {
    ...(programImage ? { programImage } : {}),
    ...(channelImageS3Key ? { channelImageS3Key } : {}),
  };
}

/**
 * Resolve thumbnail: podcast_programs first (API row + catalog by name),
 * then channel image when no program match exists.
 */
export function resolveLivestreamProgramRowImage(
  row: ExplorerProgramRankingRow,
  imageByProgramName: Map<string, PodcastProgramImageMeta>,
  imageByChannelId?: Map<string, string | undefined>,
): PodcastProgramImageMeta | undefined {
  const { programImage, channelImageS3Key } = resolveLivestreamProgramRowImages(
    row,
    imageByProgramName,
    imageByChannelId,
  );
  if (programImage) return programImage;
  if (channelImageS3Key) return { imageS3Key: channelImageS3Key };
  return undefined;
}

export function resolveLivestreamProgramRowImageS3Key(
  row: ExplorerProgramRankingRow,
  imageByProgramName: Map<string, PodcastProgramImageMeta>,
  imageByChannelId?: Map<string, string | undefined>,
): string | undefined {
  return resolveLivestreamProgramRowImage(
    row,
    imageByProgramName,
    imageByChannelId,
  )?.imageS3Key;
}
